import { z } from "zod";
import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";

const number = z.number().finite();
const point = z.object({ x: number, y: number }).passthrough();
const shape = z
  .object({
    id: z.string().min(1),
    type: z.enum([
      "rectangle",
      "circle",
      "text",
      "image",
      "svg",
      "icon",
      "furniture",
      "dynamic-furniture",
      "spot",
      "wall",
      "wall-path",
      "zone",
      "group"
    ]),
    x: number,
    y: number,
    width: number.nonnegative(),
    height: number.nonnegative(),
    layerType: z.string(),
    rotation: number.optional()
  })
  .passthrough()
  .superRefine((item, ctx) => {
    const check = (schema: z.ZodType, value: unknown, field: string) => {
      const result = schema.safeParse(value);
      if (!result.success)
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: `Invalid ${item.type} ${field}`
        });
    };
    if (item.type === "wall-path") {
      check(z.array(point).min(2), item.vertices, "vertices");
      check(z.array(z.object({}).passthrough()), item.edges, "edges");
      check(number.positive(), item.thickness, "thickness");
      if (
        Array.isArray(item.vertices) &&
        Array.isArray(item.edges) &&
        item.edges.length !== item.vertices.length - (item.closed ? 0 : 1)
      )
        ctx.addIssue({
          code: "custom",
          path: ["edges"],
          message: "Wall edge count does not match its vertices"
        });
    }
    if (["image", "svg"].includes(item.type))
      check(z.string(), item.path, "path");
    if (item.type === "text") check(z.string(), item.text, "text");
    if (item.type === "circle")
      check(number.nonnegative(), item.radius, "radius");
    if (item.type === "furniture")
      check(
        z.object({ url: z.string() }).passthrough(),
        item.furniture,
        "furniture"
      );
    if (item.type === "dynamic-furniture")
      check(
        z
          .object({
            version: z.literal(1),
            shape: z.enum(["round", "rectangle"]),
            tableWidth: number.positive(),
            tableHeight: number.positive(),
            chairs: number.int().nonnegative(),
            top: number.int().nonnegative(),
            bottom: number.int().nonnegative(),
            left: number.int().nonnegative(),
            right: number.int().nonnegative(),
            chairScale: number.positive(),
            gap: number.nonnegative(),
            cornerRadius: number.nonnegative(),
            startAngle: number,
            chairStyle: z.enum(["standard", "large"])
          })
          .passthrough(),
        item.layout,
        "layout"
      );
  });
const mapSchema = z
  .object({
    editingZoneWidth: number.positive(),
    editingZoneHeight: number.positive(),
    gridSize: number.positive(),
    shapes: z.array(shape).max(20000),
    spotLabelPattern: z.array(z.unknown()),
    lastSpotLabel: z.array(z.string()).nullable()
  })
  .passthrough();

/** Read our Download JSON output, or a single result containing `map`. No AI calls. */
export function readImportedMapJson(text: string): MapCanvasValue {
  if (text.length > 20 * 1024 * 1024)
    throw Error("Map JSON must be under 20 MB");
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw Error("This file is not valid JSON");
  }
  if (raw && typeof raw === "object" && "map" in raw) raw = raw.map;
  const result = mapSchema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    throw Error(
      `Invalid map JSON at ${first.path.join(".")}: ${first.message}. Choose a map downloaded with Download JSON.`
    );
  }
  const ids = new Set<string>();
  for (const item of result.data.shapes) {
    if (ids.has(item.id)) throw Error(`Duplicate shape ID: ${item.id}`);
    ids.add(item.id);
  }
  return result.data as unknown as MapCanvasValue;
}
